
import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme, useColor } from '../context/ColorContext';
import { ArrowRight, Grid3x3 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { categoryService } from '../services/api';

const CollectionSkeleton = ({ isDark }) => (
    <div className={`h-[450px] rounded-[2.5rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} animate-pulse overflow-hidden relative`}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-white/10" />
        <div className="absolute inset-x-0 bottom-0 p-8 space-y-4">
            <div className="h-8 w-2/3 bg-white/20 rounded-lg" />
            <div className="h-4 w-full bg-white/10 rounded-md" />
            <div className="h-4 w-1/2 bg-white/10 rounded-md" />
            <div className="flex items-center gap-2 pt-4">
                <div className="flex -space-x-2">
                    {[1, 2, 3, 4, 5].map(i => <div key={i} className="w-6 h-6 rounded-full bg-white/20 border-2 border-transparent" />)}
                </div>
                <div className="h-3 w-20 bg-white/10 rounded-full" />
            </div>
        </div>
    </div>
);

const Kartelalar = () => {
    const { theme } = useTheme();
    const { brandData, isDbLoading } = useColor();
    const isDark = theme === 'dark';
    const COLLECTIONS = brandData.COLLECTIONS;

    // State for active category
    const [activeCategory, setActiveCategory] = React.useState('all');

    // Fetch categories
    const { data: categories = [], isLoading: isCategoriesLoading } = useQuery({
        queryKey: ['categories'],
        queryFn: categoryService.getAll
    });

    // Filter collections by category
    const filteredCollections = React.useMemo(() => {
        if (activeCategory === 'all') return COLLECTIONS;
        return COLLECTIONS.filter(collection =>
            collection.categories && collection.categories.some(c => c.id === activeCategory)
        );
    }, [COLLECTIONS, activeCategory]);

    return (
        <div className={`min-h-screen pt-32 pb-20 px-4 md:px-0 container mx-auto ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <div className="text-center mb-10">
                <span className="text-sm font-bold tracking-[0.3em] opacity-60 uppercase">Koleksiyonlar</span>
                <h1 className="text-4xl md:text-6xl font-black uppercase mt-4 mb-6">Kartelalar</h1>
                <p className="max-w-xl mx-auto opacity-70 mb-8">
                    Yaşam alanlarınızı dönüştürecek özel renk koleksiyonlarımızı keşfedin. Her kartela farklı bir hikaye anlatır.
                </p>
            </div>

            {/* Category Tabs */}
            <div className="mb-12 flex flex-wrap justify-center gap-3">
                <button
                    onClick={() => setActiveCategory('all')}
                    className={`px-6 py-3 rounded-full font-black uppercase tracking-widest text-xs transition-all ${activeCategory === 'all'
                            ? isDark ? 'bg-white text-slate-900 shadow-xl scale-105' : 'bg-slate-900 text-white shadow-xl scale-105'
                            : isDark ? 'bg-white/5 hover:bg-white/10 text-white/70' : 'bg-black/5 hover:bg-black/10 text-slate-600'
                        }`}
                >
                    Tümü
                </button>
                {categories.map((category) => (
                    <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={`px-6 py-3 rounded-full font-black uppercase tracking-widest text-xs transition-all ${activeCategory === category.id
                                ? isDark ? 'bg-white text-slate-900 shadow-xl scale-105' : 'bg-slate-900 text-white shadow-xl scale-105'
                                : isDark ? 'bg-white/5 hover:bg-white/10 text-white/70' : 'bg-black/5 hover:bg-black/10 text-slate-600'
                            }`}
                    >
                        {category.name}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {isDbLoading && COLLECTIONS.length === 0 ? (
                    [1, 2, 3, 4, 5, 6].map(i => <CollectionSkeleton key={i} isDark={isDark} />)
                ) : filteredCollections.length === 0 && !isDbLoading ? (
                    <div className="col-span-full py-20 text-center opacity-60">
                        <Grid3x3 size={48} className="mx-auto mb-4 opacity-50" />
                        <h3 className="text-xl font-black uppercase tracking-widest">Bu kategoride kartela bulunamadı</h3>
                    </div>
                ) : (
                    filteredCollections.map((collection, index) => {
                        const collectionColors = collection.colors || brandData.COLORS.slice(0, 3);
                        const color1 = collectionColors[0]?.code || '#ccc';
                        const color2 = collectionColors[1]?.code || color1;
                        const color3 = collectionColors[2]?.code || color2;

                        return (
                            <Link
                                to={`/kartelalar/${collection.slug || collection.id}`}
                                key={collection.id}
                                style={{ animationDelay: `${index * 100}ms` }}
                                className="group relative h-[450px] rounded-[2.5rem] overflow-hidden cursor-pointer border border-white/10 shadow-2xl animate-fadeIn opacity-0"
                            >
                                {/* Background Image / Gradient */}
                                <div
                                    className="absolute inset-0 transition-transform duration-1000 group-hover:scale-105"
                                    style={collection.image ? {
                                        backgroundColor: isDark ? '#111' : '#f8fafc'
                                    } : {
                                        background: `linear-gradient(135deg, ${color1} 0%, ${color2} 50%, ${color3} 100%)`
                                    }}
                                >
                                    {collection.image && (
                                        <>
                                            {/* Background Fill (Blurred version of the same image) */}
                                            <div
                                                className="absolute inset-0 blur-3xl opacity-50 scale-110"
                                                style={{
                                                    backgroundImage: `url(${collection.image})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center'
                                                }}
                                            />
                                            {/* Main Sharp Image - Starts directly from Top, fits horizontal width */}
                                            <div
                                                className="absolute inset-0 z-10"
                                                style={{
                                                    backgroundImage: `url(${collection.image})`,
                                                    backgroundSize: '100% auto',
                                                    backgroundPosition: 'top',
                                                    backgroundRepeat: 'no-repeat'
                                                }}
                                            />
                                        </>
                                    )}
                                    {/* Subtle Texture Overlay */}
                                    <div className="absolute inset-0 z-20 opacity-20 bg-[url('/noise.svg')] mix-blend-overlay" />
                                </div>

                                {/* Main Hover Overlay */}
                                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-500" />

                                {/* Glassmorphic Content Panel (Bottom) */}
                                <div className="absolute inset-x-0 bottom-0 p-1 flex flex-col z-30">
                                    <div className={`relative overflow-hidden backdrop-blur-3xl rounded-[2.2rem] p-8 border-t border-white/20 ${isDark ? 'bg-black/60' : 'bg-white/40'} transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-2xl`}>

                                        {/* Artistic Color Cards Background (Mini Fan-Deck strips) */}
                                        <div className="absolute right-0 top-0 bottom-0 w-1/2 overflow-hidden opacity-40 pointer-events-none">
                                            <div className="relative h-full">
                                                {(collection.colors?.length > 0 ? collection.colors : brandData.COLORS).slice(0, 8).map((c, i) => (
                                                    <div
                                                        key={c.id || i}
                                                        className="absolute w-12 h-36 rounded-full border border-white/30 shadow-2xl transition-all duration-700 group-hover:scale-105"
                                                        style={{
                                                            backgroundColor: c.code || c.hex || '#ddd',
                                                            right: `${i * 14 - 20}px`,
                                                            bottom: `${i * 4 - 30}px`,
                                                            transformOrigin: 'bottom center',
                                                            transform: `rotate(${i * 10 - 25}deg)`,
                                                            zIndex: i,
                                                            boxShadow: '0 15px 25px -5px rgba(0, 0, 0, 0.2)'
                                                        }}
                                                    >
                                                        {/* Pivot Hole/Pin decoration */}
                                                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-white/40 shadow-inner" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Panel Content (Text on top of Cards) */}
                                        <div className="relative z-10 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-2xl font-black text-white uppercase tracking-tight drop-shadow-lg">
                                                    {collection.name?.toLocaleUpperCase('tr-TR')}
                                                </h3>
                                                <div className="w-10 h-10 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-500 scale-50 group-hover:scale-100 shadow-xl">
                                                    <ArrowRight size={20} />
                                                </div>
                                            </div>

                                            <p className="text-white/95 line-clamp-2 text-sm font-bold leading-relaxed max-w-[85%] drop-shadow-md">
                                                {collection.subtitle || collection.description || `${collection.name} özel renk koleksiyonu ile mekanlarınıza karakter katın.`}
                                            </p>

                                            {/* Color Info Badge */}
                                            <div className="flex items-center gap-2 pt-1">
                                                <div className="flex -space-x-2">
                                                    {(collection.colors?.length > 0 ? collection.colors : brandData.COLORS).slice(0, 5).map((c, i) => (
                                                        <div
                                                            key={c.id || i}
                                                            className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                                                            style={{ backgroundColor: c.code || c.hex }}
                                                        />
                                                    ))}
                                                </div>
                                                <span className="text-[9px] font-black text-white uppercase tracking-widest ml-3 bg-white/20 px-3 py-1.5 rounded-full backdrop-blur-xl border border-white/20">
                                                    {(collection.colors?.length || brandData.COLORS.length)} RENK SEÇENEĞİ
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Floating Badge (Top Right) */}
                                <div className="absolute top-6 right-6 px-4 py-2 rounded-full border border-white/20 backdrop-blur-md bg-white/10 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-lg">
                                    {collection.code || 'KOLEKSİYON'}
                                </div>
                            </Link>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default Kartelalar;
